import React from 'react';
import { useGetMatchDetail } from '../../../components/matches/matchesHooks';
import { MatchStatus, displayRoundDetail } from '@iziping/common';
import type { MatchComposition, MatchSlot } from '@iziping/common';
import styles from './MatchCard.module.css';

interface MatchCardProps {
    slot: MatchSlot;
    anchorRect: DOMRect;
    onClose: () => void;
}

const STATUS_LABEL: Record<string, string> = {
    [MatchStatus.PENDING]: 'En attente',
    [MatchStatus.RUNNING]: 'En cours',
    [MatchStatus.FINISHED]: 'Terminé',
};

const formatTime = (date: Date | string) => {
    const d = new Date(date);
    return `${d.getHours()}h${String(d.getMinutes()).padStart(2, '0')}`;
};

const MatchCard: React.FC<MatchCardProps> = ({ slot, anchorRect, onClose }) => {
    const { data: match, isLoading } = useGetMatchDetail(slot.id);

    const left = Math.min(Math.max(anchorRect.left + anchorRect.width / 2, 170), window.innerWidth - 170);
    const top = anchorRect.top - 8;

    return (
        <>
            <div className={styles.overlay} onClick={onClose} />
            <div className={styles.card} style={{ left, top }}>
                <button className={styles.closeBtn} onClick={onClose}>
                    ×
                </button>

                {isLoading && <p className={styles.loading}>Chargement…</p>}

                {!isLoading && match && (
                    <>
                        <div className={styles.header}>
                            <span className={styles.heat}>{match.heatName}</span>
                            <span className={styles.round}>{displayRoundDetail(match)}</span>
                            <span className={`${styles.badge} ${styles[match.status.toLowerCase()]}`}>
                                {STATUS_LABEL[match.status] ?? match.status}
                            </span>
                        </div>

                        <div className={styles.time}>
                            {formatTime(slot.begin)} → {formatTime(slot.end)}
                            <span className={styles.duration}>{slot.duration} min</span>
                        </div>

                        <div className={styles.opponents}>
                            {match.opponents.map((opp: MatchComposition, i) => (
                                <React.Fragment key={opp.id}>
                                    {i > 0 && <div className={styles.vs}>VS</div>}
                                    <div className={`${styles.opponent} ${opp.qualified ? styles.qualified : ''}`}>
                                        <div className={styles.players}>
                                            {opp.heatRegistration.players.map((p) => (
                                                <span key={p.id} className={styles.playerName}>
                                                    {p.firstname} {p.lastname}
                                                    {p.points != null && (
                                                        <span className={styles.points}>{p.points} pts</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                        {opp.qualified && (
                                            <span className={styles.qualifiedBadge}>
                                                {match.status === MatchStatus.FINISHED && i === 0 ? '🏆' : '✓'}
                                            </span>
                                        )}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default MatchCard;
